import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { useConvexProjectSales } from "../../../services/convexService";
import { Id } from "../../../../backend/_generated/dataModel";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useAuth } from "../../../contexts/AuthContext"; // Import the useAuth hook

interface ProjectSaleModalProps {
  projectId: Id<"projects">;
  projectTitle: string;
  isOpen: boolean;
  onClose: () => void;
  onSaleCreated: () => void;
}

const ProjectSaleModal = ({
  projectId,
  projectTitle,
  isOpen,
  onClose,
  onSaleCreated
}: ProjectSaleModalProps) => {
  const [saleAmount, setSaleAmount] = useState("");
  const [saleAgreement, setSaleAgreement] = useState("");
  const [loading, setLoading] = useState(false);
  const { createProjectSale } = useConvexProjectSales();
  const { t, language } = useLanguage();
  const { user } = useAuth(); // Use the hook at the component level

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amount = parseFloat(saleAmount);

    if (isNaN(amount) || amount <= 0) {
      toast.error(t("projectSale.errors.invalidAmount") || "Please enter a valid positive sale amount in DZD");
      return;
    }

    setLoading(true);

    try {
      // Get the current user from the auth context
      if (!user) {
        throw new Error("User not found in session. Please log in first.");
      }

      // Create the project sale listing
      const projectSaleId = await createProjectSale({
        projectId,
        sellerId: user.id,
        saleAmount: amount,
        saleAgreement: saleAgreement || undefined,
      });

      if (projectSaleId) {
        toast.success(t("projectSale.success.created") || "Project sale listing created successfully!");
        onClose();
        onSaleCreated();
      } else {
        toast.error(t("projectSale.errors.createFailed") || "Failed to create project sale listing");
      }
    } catch (error: any) {
      console.error("Error creating project sale:", error);
      const errorMessage = error.message || t("projectSale.errors.createFailed") || "Failed to create project sale listing. Please try again or contact support if the problem persists.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Define language-specific static text
  const staticTexts = {
    en: {
      title: `Sell Project ${projectTitle}`,
      amountLabel: "Sale Amount",
      amountPlaceholder: "Enter sale amount",
      amountDescription: "Enter the total amount you want to sell your project for",
      agreementLabel: "Sale Agreement",
      agreementPlaceholder: "Add any special terms or conditions for the sale...",
      agreementDescription: "Include any specific terms, conditions, or agreements related to the project sale",
      processTitle: "Project Sale Process",
      processStep1: "Create a listing for your project with the desired sale price",
      processStep2: "Investors can view your listing and accept the offer",
      processStep3: "Once accepted, the project ownership will be transferred",
      submitting: "Submitting...",
      submitRequest: "List Project for Sale",
      cancel: "Cancel"
    },
    ar: {
      title: `بيع المشروع ${projectTitle}`,
      amountLabel: "مبلغ البيع",
      amountPlaceholder: "أدخل مبلغ البيع",
      amountDescription: "أدخل المبلغ الإجمالي الذي ترغب في بيع مشروعك به",
      agreementLabel: "اتفاقية البيع",
      agreementPlaceholder: "أضف أي شروط أو أحكام خاصة للبيع...",
      agreementDescription: "قم بتضمين أي شروط أو أحكام أو اتفاقيات محددة تتعلق ببيع المشروع",
      processTitle: "عملية بيع المشروع",
      processStep1: "إنشاء قائمة لمشروعك مع سعر البيع المطلوب",
      processStep2: "يمكن للمستثمرين عرض قائمتك وقبول العرض",
      processStep3: "بمجرد القبول، سيتم نقل ملكية المشروع",
      submitting: "جاري التقديم...",
      submitRequest: "قائمة المشروع للبيع",
      cancel: "إلغاء"
    }
  };

  const texts = staticTexts[language as keyof typeof staticTexts] || staticTexts.en;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center border-b p-4">
          <h3 className="text-lg font-semibold">
            {t("projectSale.title") || texts.title}
          </h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="saleAmount">
                {t("projectSale.amountLabel") || texts.amountLabel}
              </Label>
              <Input
                id="saleAmount"
                type="number"
                value={saleAmount}
                onChange={(e) => setSaleAmount(e.target.value)}
                placeholder={t("projectSale.amountPlaceholder") || texts.amountPlaceholder}
                min="0"
                step="0.01"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("projectSale.amountDescription") || texts.amountDescription}
              </p>
            </div>

            <div>
              <Label htmlFor="saleAgreement">
                {t("projectSale.agreementLabel") || texts.agreementLabel}
              </Label>
              <textarea
                id="saleAgreement"
                value={saleAgreement}
                onChange={(e) => setSaleAgreement(e.target.value)}
                placeholder={t("projectSale.agreementPlaceholder") || texts.agreementPlaceholder}
                className="w-full p-2 border rounded-md"
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t("projectSale.agreementDescription") || texts.agreementDescription}
              </p>
            </div>

            <div className="space-y-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-800 mb-1">
                {t("projectSale.processTitle") || texts.processTitle}
              </h4>
              <ul className="text-sm text-blue-700 list-disc pl-5 space-y-1">
                <li>{t("projectSale.processStep1") || texts.processStep1}</li>
                <li>{t("projectSale.processStep2") || texts.processStep2}</li>
                <li>{t("projectSale.processStep3") || texts.processStep3}</li>
              </ul>
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                {t("common.cancel") || texts.cancel}
              </Button>
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? (t("projectSale.submitting") || texts.submitting) : (t("projectSale.submitRequest") || texts.submitRequest)}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectSaleModal;